import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CameraIcon, CheckIcon, ImageIcon, MapPinIcon, PhoneIcon, SparklesIcon, TriangleAlertIcon } from 'lucide-react';
import { intents } from '../../data/demandes';
import type { AtnaTurnKind, Intent } from '../../types/demandes';
import { LocationPicker } from './LocationPicker';

/** Adresse « détectée » par la géolocalisation simulée. */
const DETECTED_ADDRESS = 'Boulevard Grande-Allée, près de la rue Bordeaux';
interface AtnaMessageProps {
  kind: AtnaTurnKind;
  /** Only the last assistant turn keeps its actions; history is inert. */
  live: boolean;
  intent: Intent;
  address: string;
  description: string;
  answer: string | null;
  hasPhoto: boolean;
  onConfirmIntent: () => void;
  onPickIntent: (intent: Intent) => void;
  onNotEmergency: () => void;
  onPickAddress: (address: string, source: 'position' | 'profil') => void;
  onAddPhoto: () => void;
  onSkipPhoto: () => void;
  onAnswer: (answer: string) => void;
  onSend: () => void;
  onEdit: () => void;
  onReset: () => void;
}
const primaryButton = 'rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const quietButton = 'text-sm font-semibold text-slate-500 underline underline-offset-4 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
export function AtnaMessage({
  kind,
  live,
  intent,
  address,
  description,
  answer,
  hasPhoto,
  onConfirmIntent,
  onPickIntent,
  onNotEmergency,
  onPickAddress,
  onAddPhoto,
  onSkipPhoto,
  onAnswer,
  onSend,
  onEdit,
  onReset
}: AtnaMessageProps) {
  if (kind === 'urgence') {
    return <div className="border-l-4 border-red-600 pl-4" data-id="element-832" data-mp="1sdsakiu2i8d2">
        <p className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" data-id="element-833" data-mp="1xdwu6hq9vj7i">
          <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-834" data-mp="10mm9o06iw6mq" />
          Urgence possible
        </p>
        <p className="mt-3 text-lg font-extrabold leading-snug text-ville-navy" data-id="element-835" data-mp="14nxza5pecrux">
          Cette situation pourrait nécessiter une intervention immédiate.
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-836" data-mp="1ehom765vku8i">
          N’attendez pas le traitement d’une demande en ligne. Composez le 911 si une personne est en danger, ou la
          ligne d’urgence municipale pour un bris dangereux sur la voie publique.
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-837" data-mp="efrxke520aqa">
            <a href="tel:911" className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-838" data-mp="9gxfxfitos7r">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-839" data-mp="ilesjkbyc12" />
              Appeler le 911
            </a>
            <a href="tel:4509236311" className="inline-flex items-center gap-2 rounded-full border border-ville-navy px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white" data-id="element-840" data-mp="3romxneaje9x9">
              Ligne d’urgence municipale
            </a>
            <button type="button" onClick={onNotEmergency} className={quietButton} data-id="element-841" data-mp="38vakhi6oxtih">
              Ce n’est pas une urgence, continuer
            </button>
          </div>}
      </div>;
  }
  if (kind === 'comprehension') {
    return <div data-id="element-842" data-mp="17w79cs976jed">
        <p className={eyebrow} data-id="element-843" data-mp="1bvohjtg0dun8">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-844" data-mp="1bvxyysmzmthb" />
          J’ai compris :
        </p>
        <p className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-845" data-mp="y52tzrfm0wj0">{intent.label}</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-846" data-mp="132336b7rafec">{intent.service}</p>
        <p className="mt-3 text-[15px] text-slate-600" data-id="element-847" data-mp="o5lrtjzkh0rs">Est-ce bien le problème que vous souhaitez signaler ?</p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-2.5" data-id="element-848" data-mp="t5zu0lehlghw">
            <button type="button" onClick={onConfirmIntent} className={primaryButton} data-id="element-849" data-mp="f5ytx861qd3h">
              Oui
            </button>
            {intents.filter(option => option.label !== intent.label).slice(0, 3).map(option => <button key={option.label} type="button" onClick={() => onPickIntent(option)} className={chipButton} data-id="element-850" data-mp="2eij99dxf7joj">
                  {option.label}
                </button>)}
          </div>}
      </div>;
  }
  if (kind === 'lieu') {
    return <div data-id="element-851" data-mp="2jladapfusj37">
        <p className={eyebrow} data-id="element-852" data-mp="2jlk5mjodf2c2">
          <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-853" data-mp="2xfbc1j6t263k" />
          Où se trouve le problème ?
        </p>
        {live ? <>
            <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-854" data-mp="2sms34zj2bz53">Choisissez l’endroit en un clic.</p>
            <LocationPicker onPick={onPickAddress} detectedAddress={DETECTED_ADDRESS} data-id="element-855" data-mp="37f2blza83bai" />
          </> : <p className="mt-2 text-lg font-extrabold text-ville-navy" data-id="element-856" data-mp="32g8zaa6o9qbx">{address}</p>}
      </div>;
  }
  if (kind === 'photo') {
    return <div data-id="element-857" data-mp="3cddko7sp1hro">
        <p className={eyebrow} data-id="element-858" data-mp="3cck98tisi437">
          <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-859" data-mp="3ca7rohgy1vqo" />
          Photo
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-860" data-mp="1m1pilltwxic8">
          Une photo pourrait aider l’équipe à traiter votre demande.
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-861" data-mp="3h42utryqupo2">
            <button type="button" onClick={onAddPhoto} className={`inline-flex items-center gap-2 ${primaryButton}`} data-id="element-862" data-mp="3m1cmzfdipffl">
              <CameraIcon className="h-4 w-4" aria-hidden="true" data-id="element-863" data-mp="3qzxodi4ia55m" />
              Ajouter une photo
            </button>
            <button type="button" onClick={onSkipPhoto} className={quietButton} data-id="element-864" data-mp="3w1mvimolxyof">
              Continuer sans photo
            </button>
          </div>}
      </div>;
  }
  if (kind === 'detail') {
    return <div data-id="element-865" data-mp="8szozshdooog">
        <p className={eyebrow} data-id="element-866" data-mp="dm2jq3xw6468">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-867" data-mp="dn3xwha7f6s6" />
          Une dernière précision
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-868" data-mp="ijuct1a9vwc0">{intent.question}</p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-869" data-mp="nljswvccr06y">
            {intent.answers.map(option => <li key={option} data-id="element-870" data-mp="rmddbeql5wv3">
                <button type="button" onClick={() => onAnswer(option)} className={chipButton} data-id="element-871" data-mp="rjhdfkv6gy40">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'revue') {
    return <div data-id="element-872" data-mp="233q2lhvcwvrx">
        <p className="text-sm font-bold uppercase tracking-wider text-ville-indigo" data-id="element-873" data-mp="233g9hlf0m8i0">Votre demande est prête</p>
        <h3 className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-874" data-mp="1y67t9qdfgeje">{intent.label}</h3>

        <dl className="mt-4 space-y-3 text-sm" data-id="element-875" data-mp="2qssgqfqs86ho">
          <div className="flex gap-3" data-id="element-876" data-mp="2qu3loivyrkad">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-877" data-mp="2qv4x4ogw68sm">Lieu</dt>
            <dd className="flex items-center gap-1.5 font-semibold text-ville-navy" data-id="element-878" data-mp="2m0hhzg7s2qqp">
              <MapPinIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-879" data-mp="2h1xx7j4x7kzx" />
              {address}
            </dd>
          </div>
          <div className="flex gap-3" data-id="element-880" data-mp="3ekg2ykd3mtdm">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-881" data-mp="39r4r24tfjmeu">Description</dt>
            <dd className="font-semibold text-ville-navy" data-id="element-882" data-mp="2i420si6hyk8u">{description}</dd>
          </div>
          {answer && <div className="flex gap-3" data-id="element-883" data-mp="2muigk8xsihyd">
              <dt className="w-24 shrink-0 text-slate-500" data-id="element-884" data-mp="2mwd0bjxbs40v">Précision</dt>
              <dd className="font-semibold text-ville-navy" data-id="element-885" data-mp="2946lq29tm53j">{answer}</dd>
            </div>}
          <div className="flex gap-3" data-id="element-886" data-mp="2d47c8la726qy">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-887" data-mp="2d58lck7ngj61">Photo</dt>
            <dd className="font-semibold text-ville-navy" data-id="element-888" data-mp="30vmigp53d87h">{hasPhoto ? '1 photo jointe' : 'Aucune photo'}</dd>
          </div>
          <div className="flex gap-3" data-id="element-889" data-mp="35sv0mpq8pc8q">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-890" data-mp="2ru5358zidaju">Coordonnées</dt>
            <dd className="text-slate-600" data-id="element-891" data-mp="2rvzcdq1f3e83">
              Charlotte Bouchard ·{' '}
              <Link to="/mon-profil" className="font-bold text-ville-indigo hover:underline" data-id="element-892" data-mp="2wr4xwnqwqqvn">
                Modifier dans Mon profil
              </Link>
            </dd>
          </div>
        </dl>

        {live && <div className="mt-5 flex flex-wrap items-center gap-4" data-id="element-893" data-mp="1lbgbbxhkftvd">
            <button type="button" onClick={onSend} className={primaryButton} data-id="element-894" data-mp="1cv7iw2ddkbxv">
              Envoyer ma demande
            </button>
            <button type="button" onClick={onEdit} className={quietButton} data-id="element-895" data-mp="18xjjon4nfx06">
              Modifier
            </button>
          </div>}
      </div>;
  }
  return <div data-id="element-896" data-mp="1hwoizvo1bude">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-897" data-mp="1htsko689z3of">
        <CheckIcon className="h-6 w-6" data-id="element-898" data-mp="p7zhvwmzbp1n" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-899" data-mp="p5d77o4sktf7">Demande transmise</h3>
      <p className="mt-1 text-sm font-semibold text-slate-500" data-id="element-900" data-mp="z5ds3eib7hli">#REQ-2842</p>
      <p className="mt-3 text-[15px] text-slate-600" data-id="element-901" data-mp="z3ax0g2auy9x">
        Le service responsable a reçu votre demande. Nous vous informerons lorsque son statut changera.
      </p>
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-902" data-mp="6ggdbldb42xi">
          <Link to="/mes-demandes/req-2841" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-903" data-mp="2rahexm5wzff5">
            Suivre ma demande
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-904" data-mp="2cf1ft2qh8tbw" />
          </Link>
          <button type="button" onClick={onReset} className={chipButton} data-id="element-905" data-mp="28cemexe5r0cl">
            Signaler autre chose
          </button>
        </div>}
    </div>;
}